import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DataService } from 'src/app/data.service';

/**
 *  ADMIN › INTÉZMÉNY › NAPLÓZÁRÁS  (backend/naplo-zaras.js, backend/ora-naplo.js)
 *
 *  A lezárt naplóidőszak óráit nem lehet utólag átírni (téma, hiányzók, könyvelés, elmaradt,
 *  törlés) – a kiszolgáló 409-cel utasítja el. Itt állítható:
 *    - az automatikus szabály: nincs / napi (az óra napja után N nappal) / heti (a következő
 *      hét X. napján az előző hét);
 *    - kézi zárás és feloldás időszakra (a feloldás akár csak egy kollégának, lejárattal);
 *    - a törölt, már beírt órák listája és visszaállítása.
 *  Minden zárás és feloldás bejegyzés marad (ki, mikor, miért) – a visszavonás sem törli.
 */
@Component({
  selector: 'app-admin-naplozaras',
  templateUrl: './naplozaras.component.html',
  styleUrls: ['./naplozaras.component.scss'],
})
export class AdminNaplozarasComponent implements OnInit {
  intezmeny = '';
  betolt = false;
  ment = false;
  b: any = { zaras_mod: 'nincs', napi_nap: 3, heti_nap: 1, zaras_ora: 0 };
  idoszakok: any[] = [];
  maZarul: string | null = null;
  allomany: any[] = [];
  uj: any = { tipus: 'nyit', tol: '', ig: '', tanar: '', lejar: '', ok: '' };
  torolt: any[] = [];
  toroltTol = '';
  toroltIg = '';

  readonly NAPOK = [
    { e: 1, n: 'hétfő' }, { e: 2, n: 'kedd' }, { e: 3, n: 'szerda' }, { e: 4, n: 'csütörtök' },
    { e: 5, n: 'péntek' }, { e: 6, n: 'szombat' }, { e: 7, n: 'vasárnap' },
  ];
  readonly ORAK = Array.from({ length: 24 }, (_, i) => i);

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    const si: any = await this.data.getFromStorage('sajatIntezmeny');
    this.intezmeny = String(si?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
    await this.toltes();
    this.data.getAllomany(this.intezmeny).subscribe({
      next: (r: any) => {
        this.allomany = (Array.isArray(r) ? r : [])
          .map((a: any) => ({ adid: a.adid, nev: `${a.viselt_vez || ''} ${a.viselt_ker || ''}`.trim() || a.adid }))
          .sort((x: any, y: any) => x.nev.localeCompare(y.nev, 'hu'));
      },
      error: () => { this.allomany = []; },
    });
  }

  async toltes() {
    if (!this.intezmeny) return;
    this.betolt = true;
    try {
      const r: any = await firstValueFrom(this.http.get(`/api/naplo-zaras/beallitas/${this.intezmeny}`));
      this.b = r.beallitas;
      this.idoszakok = r.idoszakok || [];
      this.maZarul = r.ma_zarul || null;
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Nem sikerült betölteni a naplózárás beállítását.', 'error');
    } finally { this.betolt = false; }
  }

  napNev(e: number) { return this.NAPOK.find((x) => x.e === Number(e))?.n || ''; }

  /** A szabály egy mondatban – hogy az admin lássa, mit állít be. */
  get szabalySzoveg(): string {
    const ora = `${this.b.zaras_ora} órakor`;
    if (this.b.zaras_mod === 'napi') {
      return Number(this.b.napi_nap) === 0
        ? `Minden nap a napja utáni éjfélt követően, ${ora} zárul.`
        : `Minden nap az óra napja után ${this.b.napi_nap} nappal, a következő nap ${ora} zárul.`;
    }
    if (this.b.zaras_mod === 'heti') {
      return `Az előző hét (hétfő–vasárnap) a következő hét ${this.napNev(this.b.heti_nap)}i napján, ${ora} zárul.`;
    }
    return 'Nincs automatikus zárás – csak a kézzel lezárt időszakok zártak.';
  }

  async mentes() {
    this.ment = true;
    try {
      await firstValueFrom(this.http.put(`/api/naplo-zaras/beallitas/${this.intezmeny}`, {
        zaras_mod: this.b.zaras_mod, napi_nap: Number(this.b.napi_nap), heti_nap: Number(this.b.heti_nap),
        zaras_ora: Number(this.b.zaras_ora),
      }));
      this.data.sendToast('A naplózárás szabálya mentve.', 'success');
      await this.toltes();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Nem sikerült menteni.', 'error');
    } finally { this.ment = false; }
  }

  async idoszakFelvesz() {
    if (!this.uj.tol || !this.uj.ok?.trim()) {
      this.data.sendToast('Add meg az időszakot és az okát.', 'warning');
      return;
    }
    try {
      await firstValueFrom(this.http.post(`/api/naplo-zaras/idoszak/${this.intezmeny}`, {
        tipus: this.uj.tipus, tol: this.uj.tol, ig: this.uj.ig || this.uj.tol,
        tanar: this.uj.tipus === 'nyit' ? (this.uj.tanar || null) : null,
        lejar: this.uj.tipus === 'nyit' ? (this.uj.lejar || null) : null,
        ok: this.uj.ok.trim(),
      }));
      this.data.sendToast(this.uj.tipus === 'zar' ? 'Az időszak lezárva.' : 'Az időszak feloldva.', 'success');
      this.uj = { tipus: this.uj.tipus, tol: '', ig: '', tanar: '', lejar: '', ok: '' };
      await this.toltes();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Nem sikerült rögzíteni.', 'error');
    }
  }

  async visszavon(s: any) {
    if (!confirm(`Visszavonod ezt a ${s.tipus === 'zar' ? 'zárást' : 'feloldást'} (${s.tol} – ${s.ig})? A bejegyzés a naplóban marad.`)) return;
    try {
      await firstValueFrom(this.http.post(`/api/naplo-zaras/idoszak-visszavon/${s.id}`, {}));
      this.data.sendToast('Visszavonva.', 'success');
      await this.toltes();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Nem sikerült visszavonni.', 'error');
    }
  }

  async toroltBetolt() {
    try {
      const p = new URLSearchParams();
      if (this.toroltTol) p.set('tol', this.toroltTol);
      if (this.toroltIg) p.set('ig', this.toroltIg);
      this.torolt = await firstValueFrom(this.http.get<any[]>(`/api/ora-naplo/torolt/${this.intezmeny}?${p.toString()}`));
      if (!this.torolt.length) this.data.sendToast('Ebben az időszakban nincs törölt beírt óra.', 'info');
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A törölt órák nem tölthetők be.', 'error');
    }
  }

  async visszaallit(t: any) {
    if (!confirm(`Visszaállítod a ${t.datum} ${t.ora}. órát az órarendbe, a beírt naplóadatokkal együtt?`)) return;
    try {
      await firstValueFrom(this.http.post(`/api/ora-naplo/visszaallit/${t.id}`, {}));
      this.data.sendToast('Az óra visszaállítva.', 'success');
      await this.toroltBetolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Nem sikerült visszaállítani.', 'error');
    }
  }

  tanarNev(adid: string): string {
    return this.allomany.find((a: any) => a.adid === adid)?.nev || adid || '';
  }
}
